"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import Link from "next/link";
import {
  User, Mail, Phone, CreditCard, Calendar, Globe,
  MapPin, Home, GraduationCap, Briefcase, DollarSign,
  Upload, CheckCircle, ArrowLeft, ArrowRight, X, Check,
  Plus, Trash2, Camera, Building2, Languages, Loader2,
  RotateCcw,
} from "lucide-react";
import { submitApplication, uploadCV, uploadPhoto } from "./actions";
import type { EducationEntry, ExperienceEntry, LanguageEntry } from "./actions";
import { NATIONALITIES } from "@/lib/locations-ar";
import {
  COUNTRIES as RESIDENCE_COUNTRIES,
  getProvinces as getProvincesForCountry,
  getCities as getCitiesForProvince,
  type CountryCode,
} from "@/lib/locations-latam";
import { Turnstile } from "@/components/Turnstile";
import { isTurnstileEnabled } from "@/lib/turnstile";

// ── Types ──────────────────────────────────────────────────────
type Company = {
  id: string; name: string; slug: string;
  logo_url: string | null; primary_color: string; accent_color: string;
};
type CustomQuestion = {
  id: string; type: "text" | "textarea" | "select" | "multiselect" | "boolean";
  label: string; required: boolean; options?: string[];
};
type Job = {
  id: string; title: string; custom_questions: CustomQuestion[]; company_id: string;
  apply_form_config?: { require_salary_expectation?: boolean } | null;
};

type EdEntry = EducationEntry & { id: string; actual: boolean; mesInicio: string; anioInicio: string; mesFin: string; anioFin: string; };
type ExpEntry = ExperienceEntry & { id: string; actual: boolean; mesInicio: string; anioInicio: string; mesFin: string; anioFin: string; };
type LangEntry = LanguageEntry & { id: string; };

// ── Constants ──────────────────────────────────────────────────
const MONTHS = ["01","02","03","04","05","06","07","08","09","10","11","12"];
const MONTH_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const CY = new Date().getFullYear();
const YEARS = Array.from({ length: 51 }, (_, i) => String(CY - i));
const YEARS_FUT = Array.from({ length: 56 }, (_, i) => String(CY + 5 - i));

const COUNTRIES = [
  "Argentina","Bolivia","Brasil","Chile","Colombia","Costa Rica","Cuba","Ecuador",
  "El Salvador","España","Guatemala","Honduras","México","Nicaragua","Panamá",
  "Paraguay","Perú","República Dominicana","Uruguay","Venezuela","Otro",
];
const GENDERS = ["Prefiero no decir","Masculino","Femenino","No binario","Otro"];
const DOCUMENT_TYPES = ["DNI", "Pasaporte", "Cédula", "CUIT/CUIL", "Otro"] as const;
type DocumentType = typeof DOCUMENT_TYPES[number];

const EDUCATION_AREAS = [
  "Administración y Negocios","Arte y Diseño","Ciencias Básicas","Ciencias Sociales",
  "Comunicación y Medios","Contabilidad y Finanzas","Derecho","Economía","Educación",
  "Gastronomía y Hotelería","Humanidades","Informática y Tecnología","Ingeniería",
  "Marketing y Publicidad","Medicina y Ciencias de la Salud","Psicología",
  "Recursos Humanos","Salud y Enfermería","Seguridad e Higiene","Turismo","Otro",
];
const EDUCATION_LEVELS = ["Secundario","Tecnicatura","Terciario","Universitario","Posgrado","Máster","Doctorado","Otro"];
const EDUCATION_STATUS = ["En curso","Abandonado","Graduado"];
const LANG_LEVELS = ["Básico","Intermedio","Avanzado","Nativo"];
const COMMON_LANGUAGES = ["Español","Inglés","Portugués","Francés","Alemán","Italiano","Chino","Japonés","Árabe","Otro"];
const HIERARCHY = ["Pasante / Trainee","Junior","Semi Senior","Senior","Team Lead","Coordinador","Supervisor","Jefe / Responsable","Gerente","Director","C-Level / Socio","Freelance / Independiente"];
const INDUSTRIES = [
  "Agropecuario","Automotriz","Consultoría","Construcción y Real Estate","Educación",
  "Energía y Utilities","Farmacéutica","Finanzas y Banca","Gastronomía y Hotelería",
  "Gobierno y Sector Público","Logística y Transporte","Manufactura e Industria",
  "Marketing y Publicidad","Medios y Comunicación","ONG y Tercer Sector",
  "Retail y Comercio","Salud y Medicina","Tecnología e IT","Telecomunicaciones","Otro",
];
const WORK_AREAS = [
  "Administración","Arte y Diseño","Atención al Cliente","Ciencia y Tecnología",
  "Comercial / Ventas","Comunicación y Marketing","Contabilidad y Finanzas",
  "Dirección y Management","Educación y Formación","Ingeniería","Legal y Compliance",
  "Logística y Supply Chain","Operaciones","Producción","Recursos Humanos",
  "Salud","Sistemas e IT","Soporte Técnico","Otro",
];

const STEPS_APPLY = [
  { label: "Datos personales",        icon: User },
  { label: "Formación y experiencia", icon: GraduationCap },
  { label: "Preguntas y envío",       icon: CheckCircle },
];
// Talent pool no tiene step 3 (custom questions del job no aplican) — el
// consent + submit se hace al final del step 2.
const STEPS_TALENT_POOL = [
  { label: "Datos personales",        icon: User },
  { label: "Formación, CV y envío",   icon: GraduationCap },
];

// ── Helpers ────────────────────────────────────────────────────
function uid() { return Math.random().toString(36).slice(2, 9); }

// Fecha máxima permitida para la fecha de nacimiento: hoy menos 18 años exactos.
// Talenv es estrictamente 18+ — alguien con 17 años y 364 días NO puede postular.
function maxBirthDateForAdult(): string {
  const t = new Date();
  const d = new Date(t.getFullYear() - 18, t.getMonth(), t.getDate());
  return d.toISOString().slice(0, 10); // YYYY-MM-DD para input[type=date]
}

// Edad cumplida al día de hoy. Devuelve null si la fecha es inválida o futura.
function ageInYears(birthIsoYmd: string): number | null {
  if (!birthIsoYmd) return null;
  const [y, m, d] = birthIsoYmd.split("-").map(Number);
  if (!y || !m || !d) return null;
  const today = new Date();
  let age = today.getFullYear() - y;
  const hasHadBirthday =
    today.getMonth() + 1 > m ||
    (today.getMonth() + 1 === m && today.getDate() >= d);
  if (!hasHadBirthday) age -= 1;
  return age < 0 ? null : age;
}

function emptyEd(): EdEntry {
  // `fechaInicio`/`fechaFin` are populated from mes+anio on submit; initialize
  // them so the object satisfies both local (EdEntry) and server (EducationEntry)
  // shapes.
  return { id: uid(), titulo: "", pais: "", institucion: "", area: "", nivel: "", estado: "",
    mesInicio: "", anioInicio: "", actual: false, mesFin: "", anioFin: "", descripcion: "",
    fechaInicio: "", fechaFin: "" };
}
function emptyExp(): ExpEntry {
  return { id: uid(), puesto: "", jerarquia: "", empresa: "", pais: "", industria: "", area: "",
    subarea: "", mesInicio: "", anioInicio: "", actual: false, mesFin: "", anioFin: "", descripcion: "",
    fechaInicio: "", fechaFin: "" };
}
function emptyLang(): LangEntry {
  return { id: uid(), idioma: "", nivelOral: "", nivelEscrito: "" };
}

// ── Shared UI ──────────────────────────────────────────────────
function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-[#1A1A2E]">
        {label}{required && <span className="text-[#D94F3D] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function TInput({ icon: Icon, placeholder, value, onChange, type = "text", required, max }: {
  icon?: React.ElementType; placeholder?: string; value: string;
  onChange: (v: string) => void; type?: string; required?: boolean; max?: string;
}) {
  return (
    <div className="relative">
      {Icon && <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />}
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} max={max}
        className={`w-full ${Icon ? "pl-9" : "pl-3"} pr-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A]`} />
    </div>
  );
}

function Sel({ options, value, onChange, placeholder, required, disabled }: {
  options: string[]; value: string; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; disabled?: boolean;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} required={required} disabled={disabled}
      className="w-full px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A] text-[#1A1A2E] disabled:bg-[#F9FAFB] disabled:text-[#9CA3AF]">
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

/**
 * Combo input + dropdown sugerencias con filtro por substring (typeahead).
 *
 * A diferencia de <Sel>, el value NO está restringido al `options`. El usuario
 * puede tipear su localidad libre si no aparece en la lista (caso típico:
 * pueblos chicos del interior que no están en el dataset curado).
 *
 * UX:
 *   - Input clickeable que abre el dropdown con todas las opciones
 *   - Tipear filtra in-memory las opciones por substring (case + acento insensible)
 *   - Click en sugerencia → se setea como value
 *   - Si tipean algo que no matchea → se acepta tal cual al perder foco
 *   - Esc o click afuera cierra el dropdown
 */
function Typeahead({ options, value, onChange, placeholder, required, disabled }: {
  options: string[]; value: string; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const ref = useRef<HTMLDivElement>(null);

  // Sincronizar el query con el value cuando cambia desde afuera (ej. cambio de provincia
  // resetea la ciudad).
  useEffect(() => { setQuery(value); }, [value]);

  // Click afuera → cerrar
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Filtro insensible a acentos y caso.
  const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const filtered = query.trim().length === 0
    ? options.slice(0, 100)
    : options.filter((o) => norm(o).includes(norm(query))).slice(0, 100);

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => !disabled && setOpen(true)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        className="w-full px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A] text-[#1A1A2E] disabled:bg-[#F9FAFB] disabled:text-[#9CA3AF]"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-[#E5E7EB] rounded-xl shadow-lg">
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => { setQuery(o); onChange(o); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-[#1A1A2E] hover:bg-[#F8F9FA] focus:bg-[#F8F9FA] focus:outline-none"
            >
              {o}
            </button>
          ))}
          {filtered.length === 100 && (
            <div className="px-3 py-2 text-[11px] text-[#9CA3AF] border-t border-[#F3F4F6]">
              Mostrando primeros 100 — seguí tipeando para refinar
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MonthYear({ mesValue, anioValue, onMes, onAnio, years = YEARS }: {
  mesValue: string; anioValue: string; onMes: (v: string) => void; onAnio: (v: string) => void; years?: string[];
}) {
  return (
    <div className="flex gap-2">
      <select value={mesValue} onChange={(e) => onMes(e.target.value)}
        className="flex-1 px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20">
        <option value="">Mes</option>
        {MONTHS.map((m, i) => <option key={m} value={m}>{MONTH_NAMES[i]}</option>)}
      </select>
      <select value={anioValue} onChange={(e) => onAnio(e.target.value)}
        className="flex-1 px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20">
        <option value="">Año</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-jakarta font-semibold text-[#1A1A2E] text-base pb-2 border-b border-[#E5E7EB]">
      {children}
    </h3>
  );
}

function ErrorBox({ message }: { message: string }) {
  return <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-600">{message}</div>;
}

// ── Education card ─────────────────────────────────────────────
function EducationCard({ entry, onChange, onRemove }: {
  entry: EdEntry;
  onChange: (id: string, field: keyof EdEntry, value: string | boolean) => void;
  onRemove: (id: string) => void;
}) {
  const set = (field: keyof EdEntry) => (v: string | boolean) => onChange(entry.id, field, v);
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-[#FAFAFA] p-5 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Título">
          <TInput placeholder="Ej: Lic. en Administración" value={entry.titulo} onChange={set("titulo")} />
        </Field>
        <Field label="País">
          <Sel options={COUNTRIES} value={entry.pais} onChange={set("pais")} placeholder="Seleccioná..." />
        </Field>
        <Field label="Institución">
          <TInput placeholder="Ej: UBA, UTN..." value={entry.institucion} onChange={set("institucion")} />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Área de estudio">
          <Sel options={EDUCATION_AREAS} value={entry.area} onChange={set("area")} placeholder="Seleccioná..." />
        </Field>
        <Field label="Nivel de estudio">
          <Sel options={EDUCATION_LEVELS} value={entry.nivel} onChange={set("nivel")} placeholder="Seleccioná..." />
        </Field>
        <Field label="Estado de estudio">
          <Sel options={EDUCATION_STATUS} value={entry.estado} onChange={set("estado")} placeholder="Seleccioná..." />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-3">
          <Field label="Fecha de inicio">
            <MonthYear mesValue={entry.mesInicio} anioValue={entry.anioInicio}
              onMes={set("mesInicio")} onAnio={set("anioInicio")} />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[#1A1A2E]">
            <input type="checkbox" checked={entry.actual}
              onChange={(e) => set("actual")(e.target.checked)} className="rounded border-[#E5E7EB]" />
            Actualmente asisto
          </label>
          {!entry.actual && (
            <Field label="Año de finalización (o previsto)">
              <MonthYear mesValue={entry.mesFin} anioValue={entry.anioFin}
                onMes={set("mesFin")} onAnio={set("anioFin")} years={YEARS_FUT} />
            </Field>
          )}
        </div>
        <Field label="Descripción">
          <textarea value={entry.descripcion} onChange={(e) => set("descripcion")(e.target.value)}
            placeholder="Ingresa una descripción..." rows={5} maxLength={1000}
            className="w-full px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 resize-none" />
          <p className="text-right text-[10px] text-[#9CA3AF] -mt-0.5">{entry.descripcion.length}/1000</p>
        </Field>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={() => onRemove(entry.id)}
          className="flex items-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-600 transition-colors">
          <Trash2 size={13} /> Eliminar
        </button>
      </div>
    </div>
  );
}

// ── Experience card ────────────────────────────────────────────
function ExperienceCard({ entry, onChange, onRemove }: {
  entry: ExpEntry;
  onChange: (id: string, field: keyof ExpEntry, value: string | boolean) => void;
  onRemove: (id: string) => void;
}) {
  const set = (field: keyof ExpEntry) => (v: string | boolean) => onChange(entry.id, field, v);
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-[#FAFAFA] p-5 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Nombre del puesto / Título">
          <TInput placeholder="Ej: Desarrollador Senior" value={entry.puesto} onChange={set("puesto")} />
        </Field>
        <Field label="Jerarquía">
          <Sel options={HIERARCHY} value={entry.jerarquia} onChange={set("jerarquia")} placeholder="Seleccioná..." />
        </Field>
        <Field label="Nombre de empresa">
          <TInput placeholder="Ej: Acme S.A." value={entry.empresa} onChange={set("empresa")} />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="País">
          <Sel options={COUNTRIES} value={entry.pais} onChange={set("pais")} placeholder="Seleccioná..." />
        </Field>
        <Field label="Tipo de industria">
          <Sel options={INDUSTRIES} value={entry.industria} onChange={set("industria")} placeholder="Seleccioná..." />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Área de trabajo">
          <Sel options={WORK_AREAS} value={entry.area} onChange={set("area")} placeholder="Seleccioná..." />
        </Field>
        <Field label="Subárea de trabajo">
          <TInput placeholder="Ej: Desarrollo Web" value={entry.subarea} onChange={set("subarea")} />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-3">
          <Field label="Fecha de inicio">
            <MonthYear mesValue={entry.mesInicio} anioValue={entry.anioInicio}
              onMes={set("mesInicio")} onAnio={set("anioInicio")} />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[#1A1A2E]">
            <input type="checkbox" checked={entry.actual}
              onChange={(e) => set("actual")(e.target.checked)} className="rounded border-[#E5E7EB]" />
            Actualmente trabajo aquí
          </label>
          {!entry.actual && (
            <Field label="Fecha de finalización">
              <MonthYear mesValue={entry.mesFin} anioValue={entry.anioFin}
                onMes={set("mesFin")} onAnio={set("anioFin")} />
            </Field>
          )}
        </div>
        <Field label="Descripción">
          <textarea value={entry.descripcion} onChange={(e) => set("descripcion")(e.target.value)}
            placeholder="Ingresa una descripción..." rows={5} maxLength={1000}
            className="w-full px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 resize-none" />
          <p className="text-right text-[10px] text-[#9CA3AF] -mt-0.5">{entry.descripcion.length}/1000</p>
        </Field>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={() => onRemove(entry.id)}
          className="flex items-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-600 transition-colors">
          <Trash2 size={13} /> Eliminar
        </button>
      </div>
    </div>
  );
}

// ── Main form ──────────────────────────────────────────────────
export function ApplyForm({ company, job, slug, jobId, mode = "apply", talentPoolAvailable = false }: {
  company: Company;
  /** Job al que postula. NULL solo cuando mode='talent_pool'. */
  job?: Job | null;
  slug: string;
  /** ID del job. NULL solo cuando mode='talent_pool'. */
  jobId?: string | null;
  /** Modo del formulario:
   *  - 'apply' (default): wizard de 3 pasos. Crea una application contra el job.
   *  - 'talent_pool': mismos pasos 1 y 2 pero step 3 se reemplaza por consent
   *    + submit. Crea / actualiza fila en candidates con in_talent_pool=true.
   *    No hace falta job ni jobId. */
  mode?: "apply" | "talent_pool";
  /** ¿La empresa tiene pool de talentos habilitado? (feature_talent_pool_enabled
   *  o plan_configs.talent_pool). Si false, NO se renderiza el checkbox 2 de
   *  "sumarme también al pool de [Empresa]" — el form muestra solo el
   *  consentimiento obligatorio para la postulación al puesto. */
  talentPoolAvailable?: boolean;
}) {
  const isTalentPool = mode === "talent_pool";
  const primary = company.primary_color || "#1B2A4A";
  const accent  = company.accent_color  || "#D94F3D";
  const questions: CustomQuestion[] = (job?.custom_questions as CustomQuestion[]) ?? [];

  const photoInputRef = useRef<HTMLInputElement>(null);
  const cvInputRef    = useRef<HTMLInputElement>(null);

  const [step, setStep]           = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]     = useState(false);
  const [error, setError]         = useState<string | null>(null);

  // ── Step 1 state ───────────────────────────────────────────
  // Mantenemos el campo `dni` por compatibilidad histórica de la BD: lo
  // armamos como "<documentType>: <documentNumber>" al enviar (salvo DNI
  // simple, que va sólo con el número para no romper búsquedas existentes).
  const [p, setP] = useState({
    firstName: "", lastName: "", email: "", emailConfirm: "", phone: "",
    documentType: "DNI" as DocumentType, documentNumber: "",
    birthDate: "", gender: "", nationality: "",
    // Country code (AR, BR, MX, US, etc). Default AR — la mayoría del
    // tráfico del portal viene de Argentina y evita un clic extra.
    residenceCountry: "AR" as CountryCode,
    province: "", city: "", address: "",
  });
  const [photoFile, setPhotoFile]       = useState<File | null>(null);
  const [photoUrl, setPhotoUrl]         = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // ── Step 2 state ───────────────────────────────────────────
  const [education, setEducation]   = useState<EdEntry[]>([]);
  const [secondaryComplete, setSecondaryComplete] = useState<"" | "si" | "no">("");
  const [languages, setLanguages]   = useState<LangEntry[]>([]);
  const [experience, setExperience] = useState<ExpEntry[]>([]);
  const [pro, setPro] = useState({
    linkedinUrl: "", salaryExpectation: "",
  });
  const [cvFile, setCvFile]   = useState<File | null>(null);
  const [cvError, setCvError] = useState<string | null>(null);

  // ── Step 3 state ───────────────────────────────────────────
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>({});
  // DOS consentimientos separados — granularidad estilo GDPR / lineamientos
  // AAIP 2024+:
  //   (a) companyConsent — OBLIGATORIO. Habilita a la empresa a procesar
  //       los datos para evaluar la postulación concreta. Sin esto el form
  //       no envía.
  //   (b) benchConsent — OPCIONAL y SOLO se muestra si talentPoolAvailable=true.
  //       Habilita a ESTA empresa (no a Talenv cross-tenant) a guardar el
  //       perfil en SU pool de talentos por 12 meses para considerarlo en
  //       futuras búsquedas similares. Sus datos no se comparten con otras
  //       empresas.
  // El texto literal de cada casilla se guarda junto con la postulación
  // (Art. 6 Ley 25.326 — consentimiento informado, comprobable).
  const [companyConsent, setCompanyConsent] = useState(false);
  const [benchConsent, setBenchConsent] = useState(false);

  // Anti-bot: timestamp de cuando se cargó el form (humanos tardan >3s) +
  // honeypot invisible. El server rechaza si viene con valor o si el delta
  // es < 2.5s.
  const [formLoadedAt] = useState(() => Date.now());
  const [honeypot, setHoneypot] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // ── Autosave del wizard en localStorage ─────────────────────
  // El wizard es largo (3 pasos, ~30 campos). Si el candidato cierra el
  // tab por error, se queda sin conexión, o refresca sin querer, pierde
  // TODO el progreso. Con autosave persistimos el estado del form en
  // localStorage y al volver ofrecemos "Retomar" o "Empezar de cero".
  //
  // Qué se persiste: step + p (datos personales) + education + experience
  // + languages + pro + secondaryComplete + customAnswers + consents +
  // photoUrl (URL de Storage — la foto ya está subida).
  //
  // Qué NO se persiste (transitorios o irrestaurables):
  //   - cvFile, photoFile: File objects; el browser no permite reconstruirlos
  //     desde localStorage por seguridad. Después de refresh el candidato
  //     tiene que re-seleccionar el PDF (aviso en el UI).
  //   - photoPreview: base64 de la preview local. Grande y no crítico —
  //     photoUrl (URL final) sí se persiste, así la foto se muestra igual.
  //   - submitting, success, error, formLoadedAt, honeypot, captchaToken.
  //
  // La key es scopeada por company + job (o "pool" en modo talent_pool)
  // para que aplicar a jobs distintos no cross-contamine drafts.
  const DRAFT_KEY = `talenv_apply_draft_v1__${company.id}__${jobId ?? "pool"}`;
  const [draftAvailable, setDraftAvailable] = useState(false);
  const [draftDismissed, setDraftDismissed] = useState(false);
  // Evita rehidratación en loop: solo hidratamos una vez al montar.
  const draftHydratedRef = useRef(false);

  useEffect(() => {
    if (draftHydratedRef.current) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") setDraftAvailable(true);
    } catch { /* localStorage bloqueado (modo privado) — silencioso */ }
  }, [DRAFT_KEY]);

  function hydrateFromDraft() {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.step) setStep(d.step);
      if (d.p) setP((prev) => ({ ...prev, ...d.p }));
      if (Array.isArray(d.education)) setEducation(d.education);
      if (Array.isArray(d.experience)) setExperience(d.experience);
      if (Array.isArray(d.languages)) setLanguages(d.languages);
      if (d.pro) setPro((prev) => ({ ...prev, ...d.pro }));
      if (d.secondaryComplete) setSecondaryComplete(d.secondaryComplete);
      if (d.customAnswers) setCustomAnswers(d.customAnswers);
      if (typeof d.companyConsent === "boolean") setCompanyConsent(d.companyConsent);
      if (typeof d.benchConsent === "boolean") setBenchConsent(d.benchConsent);
      if (d.photoUrl) setPhotoUrl(d.photoUrl);
    } catch { /* draft corrupto — ignoramos */ }
    draftHydratedRef.current = true;
    setDraftAvailable(false);
  }

  function discardDraft() {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
    setDraftAvailable(false);
    setDraftDismissed(true);
    draftHydratedRef.current = true;
  }

  // Save debounced — 500ms después del último cambio. Evita spammear
  // localStorage en cada keystroke. Solo empieza a guardar DESPUÉS de que
  // el usuario decidió qué hacer con el draft anterior (retomar o empezar
  // de cero) — antes de esa decisión no queremos pisar el draft viejo.
  useEffect(() => {
    if (draftAvailable) return; // hay draft pendiente, no pisar todavía
    const t = setTimeout(() => {
      try {
        const snapshot = {
          step, p, education, experience, languages, pro,
          secondaryComplete, customAnswers,
          companyConsent, benchConsent, photoUrl,
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(snapshot));
      } catch { /* quota exceeded / disabled — silencioso */ }
    }, 500);
    return () => clearTimeout(t);
  }, [
    DRAFT_KEY, draftAvailable,
    step, p, education, experience, languages, pro,
    secondaryComplete, customAnswers,
    companyConsent, benchConsent, photoUrl,
  ]);

  // ── Progress indicator ─────────────────────────────────────
  // % de completitud del wizard. Se calcula como:
  //    (steps ya cerrados + fracción del step actual) / totalSteps
  // La fracción intra-step es campos_required_llenos / campos_required_total
  // del step, contando solo lo que evalúa la validación real.
  const requireSalaryExpectationForProgress =
    job?.apply_form_config?.require_salary_expectation === true;
  const progressPct = useMemo(() => {
    const totalSteps = isTalentPool ? 2 : 3;
    let intraFilled = 0, intraTotal = 1;
    if (step === 1) {
      const req = [
        p.firstName, p.lastName, p.email, p.emailConfirm, p.phone,
        p.documentType, p.documentNumber, p.birthDate, p.nationality,
        p.province, p.city,
      ];
      intraTotal = req.length;
      intraFilled = req.filter((v) => (v ?? "").toString().trim().length > 0).length;
      // Bonus: emails coincidiendo cuenta como "1 más completo"
      if (p.email && p.emailConfirm && p.email === p.emailConfirm) intraFilled = Math.min(intraFilled, intraTotal);
    } else if (step === 2) {
      const req: string[] = [];
      if (requireSalaryExpectationForProgress) req.push(pro.salaryExpectation);
      if (isTalentPool) req.push(cvFile ? "ok" : "");
      intraTotal = Math.max(req.length, 1);
      intraFilled = req.length === 0
        ? 1
        : req.filter((v) => (v ?? "").trim().length > 0).length;
    } else if (step === 3) {
      const reqQuestions = questions.filter((q) => q.required);
      intraTotal = reqQuestions.length + 1; // +1 por consent obligatorio
      intraFilled =
        reqQuestions.filter((q) => (customAnswers[q.id] ?? "").trim().length > 0).length +
        (companyConsent ? 1 : 0);
    }
    const stepFrac = intraTotal > 0 ? intraFilled / intraTotal : 1;
    return Math.max(0, Math.min(100, Math.round(((step - 1 + stepFrac) / totalSteps) * 100)));
  }, [
    step, isTalentPool, p, pro.salaryExpectation, cvFile,
    questions, customAnswers, companyConsent,
    requireSalaryExpectationForProgress,
  ]);

  // ── Validation ─────────────────────────────────────────────
  function validateStep1(): string | null {
    if (!p.firstName.trim())    return "El nombre es obligatorio.";
    if (!p.lastName.trim())     return "El apellido es obligatorio.";
    if (!p.email.trim())        return "El email es obligatorio.";
    if (!p.emailConfirm.trim()) return "Debés confirmar el email.";
    if (p.email !== p.emailConfirm) return "Los emails no coinciden.";
    if (!p.phone.trim())        return "El teléfono es obligatorio.";
    if (!p.documentType)        return "El tipo de documento es obligatorio.";
    if (!p.documentNumber.trim()) return "El número de documento es obligatorio.";
    if (!p.birthDate)           return "La fecha de nacimiento es obligatoria.";
    // Talenv es estrictamente 18+. Bloqueamos antes de enviar para evitar
    // pedirle al candidato menor que cargue datos en el resto del flujo.
    const age = ageInYears(p.birthDate);
    if (age === null)           return "La fecha de nacimiento no es válida.";
    if (age < 18)               return "Para postular debés ser mayor de 18 años.";
    if (!p.nationality.trim())  return "La nacionalidad es obligatoria.";
    if (!p.province.trim())     return "La provincia es obligatoria.";
    if (!p.city.trim())         return "La ciudad es obligatoria.";
    return null;
  }

  // ── Photo upload ───────────────────────────────────────────
  async function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png","image/jpeg","image/webp"].includes(file.type)) {
      setError("La foto debe ser PNG, JPG o WEBP."); return;
    }
    if (file.size > 2 * 1024 * 1024) { setError("La foto no puede superar 2MB."); return; }

    // Local preview
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
    setPhotoFile(file);

    // Upload immediately
    setPhotoUploading(true);
    const b64Reader = new FileReader();
    const base64 = await new Promise<string>((resolve) => {
      b64Reader.onload = () => resolve((b64Reader.result as string).split(",")[1]);
      b64Reader.readAsDataURL(file);
    });
    const result = await uploadPhoto(company.id, file.name, base64, file.type);
    setPhotoUploading(false);
    if (!result.error) setPhotoUrl(result.url ?? null);
  }

  // ── Education helpers ──────────────────────────────────────
  function changeEd(id: string, field: keyof EdEntry, value: string | boolean) {
    setEducation((prev) => prev.map((e) => e.id === id ? { ...e, [field]: value } : e));
  }
  function removeEd(id: string) { setEducation((prev) => prev.filter((e) => e.id !== id)); }

  // ── Language helpers ───────────────────────────────────────
  function changeLang(id: string, field: keyof LangEntry, value: string) {
    setLanguages((prev) => prev.map((l) => l.id === id ? { ...l, [field]: value } : l));
  }
  function removeLang(id: string) { setLanguages((prev) => prev.filter((l) => l.id !== id)); }

  // ── Experience helpers ─────────────────────────────────────
  function changeExp(id: string, field: keyof ExpEntry, value: string | boolean) {
    setExperience((prev) => prev.map((e) => e.id === id ? { ...e, [field]: value } : e));
  }
  function removeExp(id: string) { setExperience((prev) => prev.filter((e) => e.id !== id)); }

  // ── CV ─────────────────────────────────────────────────────
  function handleCvChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    setCvError(null);
    if (!file) return;
    if (file.type !== "application/pdf") { setCvError("Solo se aceptan archivos PDF."); return; }
    if (file.size > 5 * 1024 * 1024) { setCvError("El archivo no puede superar 5MB."); return; }
    setCvFile(file);
  }

  // ── Navigation ─────────────────────────────────────────────
  const requireSalaryExpectation = job?.apply_form_config?.require_salary_expectation === true;

  function validateStep2(): string | null {
    if (requireSalaryExpectation && !pro.salaryExpectation.trim()) {
      return "La pretensión salarial es obligatoria para esta búsqueda.";
    }
    return null;
  }

  function handleNext() {
    setError(null);
    if (step === 1) {
      const err = validateStep1();
      if (err) { setError(err); return; }
    }
    if (step === 2) {
      const err = validateStep2();
      if (err) { setError(err); return; }
      // Talent pool no tiene step 3 — al avanzar desde step 2 se acerca al
      // submit (que el botón "Enviar mi CV" dispara). Acá no avanzamos.
      if (isTalentPool) return;
    }
    setStep((s) => (s + 1) as 1 | 2 | 3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleBack() {
    setError(null);
    setStep((s) => (s - 1) as 1 | 2 | 3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Textos literales que se le muestran al candidato — los persistimos
  // junto con la postulación para que cualquier reclamo posterior pueda
  // contrastarse contra exactamente lo que firmó.
  const companyConsentText = isTalentPool
    ? `He leído y acepto la política de privacidad de ${company.name}. Autorizo que guarden mi CV en su pool de talentos por hasta 12 meses para considerarme en futuras búsquedas.`
    : `He leído y acepto la política de privacidad. Autorizo a ${company.name} a procesar mis datos personales para evaluar mi postulación a "${job?.title}" bajo la Ley 25.326.`;
  // Texto del checkbox 2 — actualizado: refiere al pool de talentos DE LA
  // EMPRESA (no al Bench cross-tenant de Talenv que se discontinuó).
  // Solo se muestra si talentPoolAvailable=true.
  const benchConsentText = `Acepto que ${company.name} también guarde mi perfil en su pool de talentos por 12 meses, para considerarme en futuras búsquedas similares. Mis datos no se comparten con otras empresas. Puedo revocar este consentimiento o solicitar el borrado en cualquier momento desde el link al pie de cualquier email.`;

  // ── Submit ─────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!companyConsent) {
      setError("Tenés que aceptar que la empresa procese tus datos para postular.");
      return;
    }

    // Las casillas (multiselect) no tienen `required` nativo — validamos a mano
    // que las preguntas requeridas de ese tipo tengan al menos una opción.
    const missingMulti = questions.find(
      (q) => q.type === "multiselect" && q.required && !(customAnswers[q.id] ?? "").trim(),
    );
    if (missingMulti) {
      setError(`Elegí al menos una opción en: "${missingMulti.label}".`);
      return;
    }

    setSubmitting(true);
    setError(null);

    let cvUrl: string | undefined;
    let cvText: string | undefined;
    if (cvFile) {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve) => {
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.readAsDataURL(cvFile);
      });
      const result = await uploadCV(company.id, cvFile.name, base64);
      if (result.error) {
        setError(`Error al subir el CV: ${result.error}`);
        setSubmitting(false);
        return;
      }
      cvUrl = result.url;
      cvText = result.cvText;
    }

    // Build education entries (strip internal id + actual flag)
    const edPayload = education.map(({ id: _id, actual, mesInicio, anioInicio, mesFin, anioFin, ...rest }) => ({
      ...rest,
      fechaInicio: [mesInicio, anioInicio].filter(Boolean).join("/"),
      fechaFin: actual ? "Actualidad" : [mesFin, anioFin].filter(Boolean).join("/"),
    }));

    const expPayload = experience.map(({ id: _id, actual, mesInicio, anioInicio, mesFin, anioFin, ...rest }) => ({
      ...rest,
      fechaInicio: [mesInicio, anioInicio].filter(Boolean).join("/"),
      fechaFin: actual ? "Actualidad" : [mesFin, anioFin].filter(Boolean).join("/"),
    }));

    const langPayload = languages
      .filter((l) => l.idioma)
      .map(({ id: _id, ...rest }) => rest);

    // El documento se persiste en `dni` (columna existente) prefijado por
    // su tipo cuando NO es DNI argentino, para que el recruiter vea de un
    // vistazo qué tipo de documento cargó el candidato (ej. "Pasaporte:
    // AB1234567"). DNI a secas conserva el formato histórico.
    const composedDni = p.documentNumber.trim()
      ? p.documentType === "DNI"
        ? p.documentNumber.trim()
        : `${p.documentType}: ${p.documentNumber.trim()}`
      : undefined;

    // ── Branch por modo: talent_pool vs apply ───────────────────────────────
    if (isTalentPool) {
      if (!cvUrl) {
        setError("Cargá tu CV antes de enviar.");
        setSubmitting(false);
        return;
      }
      const { submitTalentPoolEntry } = await import("@/lib/talent-pool/actions");
      const result = await submitTalentPoolEntry({
        companySlug: slug,
        fullName: `${p.firstName.trim()} ${p.lastName.trim()}`.trim(),
        email: p.email.trim(),
        phone: p.phone || null,
        location: [p.city, p.province].filter(Boolean).join(", ") || null,
        linkedinUrl: pro.linkedinUrl || null,
        cvUrl,
        consentText: companyConsentText,
        captchaToken,
        // Datos ricos del wizard — el server los persiste en cv_data así el
        // recruiter ve el perfil completo igual que con un apply.
        dni: composedDni,
        birthDate: p.birthDate || undefined,
        gender: p.gender || undefined,
        nationality: p.nationality || undefined,
        province: p.province || undefined,
        city: p.city || undefined,
        address: p.address || undefined,
        photoUrl: photoUrl || undefined,
        education: edPayload,
        secondaryComplete: secondaryComplete === "si" ? true : secondaryComplete === "no" ? false : undefined,
        languages: langPayload,
        experience: expPayload,
        salaryExpectation: pro.salaryExpectation || undefined,
        residenceCountry: p.residenceCountry,
      });
      if (result.error) { setError(result.error); setSubmitting(false); return; }
      setSuccess(true);
      return;
    }

    const result = await submitApplication({
      jobId: job!.id,
      companyId: company.id,
      companyName: company.name,
      companySlug: company.slug,
      companyLogoUrl: company.logo_url,
      jobTitle: job!.title,
      firstName: p.firstName,
      lastName: p.lastName,
      email: p.email,
      phone: p.phone,
      dni: composedDni,
      documentType: p.documentType,
      documentNumber: p.documentNumber.trim() || undefined,
      birthDate: p.birthDate || undefined,
      gender: p.gender || undefined,
      nationality: p.nationality || undefined,
      province: p.province || undefined,
      city: p.city || undefined,
      address: p.address || undefined,
      photoUrl: photoUrl || undefined,
      education: edPayload,
      secondaryComplete: secondaryComplete === "si" ? true : secondaryComplete === "no" ? false : undefined,
      languages: langPayload,
      experience: expPayload,
      linkedinUrl: pro.linkedinUrl || undefined,
      salaryExpectation: pro.salaryExpectation || undefined,
      residenceCountry: p.residenceCountry,
      cvUrl,
      cvText,
      customAnswers,
      captchaToken,
      // Consentimientos granulares — el server persiste cada uno con su
      // propio texto, IP y user-agent, y la versión de la política vigente.
      // benchConsent solo se manda como true si la empresa tiene pool
      // habilitado (no debería pasar de otra forma porque el checkbox no
      // se renderiza, pero defensa en profundidad).
      companyConsent,
      companyConsentText,
      benchConsent: talentPoolAvailable && benchConsent,
      benchConsentText: (talentPoolAvailable && benchConsent) ? benchConsentText : null,
      // Back-compat: el campo viejo `talentPoolConsent` sigue alimentando la
      // tabla `talent_pool_consent` (Bench) — sólo se activa si el usuario
      // tildó la segunda casilla.
      talentPoolConsent: benchConsent,
      talentPoolConsentText: benchConsent ? benchConsentText : null,
      honeypot,
      formLoadedAt,
    });

    if (result.error) { setError(result.error); setSubmitting(false); return; }
    persistApplied();
    setSuccess(true);
  }

  // Al submit exitoso: borramos el draft (ya no lo necesitamos) y
  // registramos el applied en localStorage.talenv_applied_v1 — así el
  // detalle del job (_apply-cta.tsx) muestra "ya te postulaste el X" en
  // lugar del CTA la próxima vez que el candidato pase por acá.
  //
  // Solo aplica a modo apply (con jobId); en talent_pool no hay job al
  // que "haber postulado", así que no marcamos nada.
  function persistApplied() {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {}
    if (isTalentPool || !jobId || !job) return;
    try {
      const raw = localStorage.getItem("talenv_applied_v1");
      const dict = raw ? JSON.parse(raw) : {};
      dict[jobId] = {
        date: new Date().toISOString(),
        jobTitle: job.title,
        companySlug: company.slug,
      };
      localStorage.setItem("talenv_applied_v1", JSON.stringify(dict));
    } catch {}
  }

  // ── Success screen ─────────────────────────────────────────
  if (success) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full mx-auto mb-6" style={{ backgroundColor: `${accent}20` }}>
          <CheckCircle size={32} style={{ color: accent }} strokeWidth={1.5} />
        </div>
        <h1 className="font-jakarta text-2xl font-bold text-[#1A1A2E] mb-3">
          {isTalentPool ? "¡Recibimos tu CV!" : "¡Tu postulación fue enviada!"}
        </h1>
        <p className="text-[#6B7280] mb-2">
          {isTalentPool
            ? <>Sumamos tu perfil al pool de talentos de <strong>{company.name}</strong>. Te contactamos cuando encontremos una búsqueda que encaje.</>
            : <>Gracias por postularte a <strong>{job?.title}</strong> en <strong>{company.name}</strong>.</>}
        </p>
        <p className="text-sm text-[#6B7280] mb-8">Revisá tu email — te enviamos una confirmación.</p>
        <Link href={`/portal/${slug}`} className="inline-flex items-center gap-2 text-sm font-medium hover:underline" style={{ color: primary }}>
          <ArrowLeft size={14} /> Ver otras posiciones
        </Link>
      </div>
    );
  }

  // ── Step indicator + progress bar ───────────────────────────
  const STEPS = isTalentPool ? STEPS_TALENT_POOL : STEPS_APPLY;
  const stepIndicator = (
    <div className="mb-8 space-y-3">
      {/* Progress bar — muestra el % de completitud del wizard total.
          Fill = primary color del tenant para brand consistency. Se anima
          suave al cambiar (útil cuando el user llena un campo required). */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-[#6B7280]">
            Paso {step} de {STEPS.length}
          </span>
          <span className="text-xs font-semibold" style={{ color: primary }}>
            {progressPct}%
          </span>
        </div>
        <div
          className="h-1.5 rounded-full bg-[#E5E7EB] overflow-hidden"
          role="progressbar"
          aria-valuenow={progressPct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full transition-all duration-300 ease-out"
            style={{ width: `${progressPct}%`, backgroundColor: primary }}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        {STEPS.map(({ label }, i) => {
          const s = i + 1;
          const done = s < step;
          const active = s === step;
          return (
            <div key={s} className="flex items-center gap-2 flex-1 last:flex-none">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold flex-shrink-0 transition-colors ${
                done ? "bg-[#2A9D8F] text-white" : active ? "text-white" : "bg-[#E5E7EB] text-[#6B7280]"
              }`} style={active ? { backgroundColor: primary } : {}}>
                {done ? <Check size={13} /> : s}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${active ? "text-[#1A1A2E]" : "text-[#6B7280]"}`}>{label}</span>
              {s < STEPS.length && <div className="flex-1 h-px bg-[#E5E7EB] mx-1" />}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Banner "Retomar postulación" — se muestra cuando detectamos un draft
  // en localStorage al montar y el usuario todavía no decidió qué hacer
  // con él. Se oculta al elegir "Retomar" o "Empezar de cero".
  const draftBanner = draftAvailable && !draftDismissed ? (
    <div className="mb-6 rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#F59E0B]/15 flex-shrink-0">
          <RotateCcw size={16} className="text-[#B45309]" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-jakarta font-semibold text-[#78350F] text-sm">
            Tenés una postulación en curso
          </p>
          <p className="text-xs text-[#92400E] mt-0.5">
            Guardamos tu progreso automáticamente. ¿Querés seguir desde donde dejaste?
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={hydrateFromDraft}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-white text-xs font-semibold hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primary }}
            >
              Retomar
            </button>
            <button
              type="button"
              onClick={discardDraft}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-[#78350F] border border-[#FDE68A] bg-white hover:bg-[#FFFBEB] transition-colors"
            >
              Empezar de cero
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const navButtons = (hideNext?: boolean) => (
    <div className="flex justify-between pt-4">
      {step > 1 ? (
        <button type="button" onClick={handleBack}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-[#6B7280] border border-[#E5E7EB] bg-white hover:bg-gray-50 transition-colors">
          <ArrowLeft size={15} /> Atrás
        </button>
      ) : <div />}
      {!hideNext && (
        <button type="button" onClick={handleNext}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-semibold hover:opacity-90 transition-opacity"
          style={{ backgroundColor: primary }}>
          Continuar <ArrowRight size={15} />
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <Link
        href={isTalentPool ? `/portal/${slug}` : `/portal/${slug}/jobs/${jobId}`}
        className="inline-flex items-center gap-1.5 text-sm mb-8 hover:underline"
        style={{ color: primary }}
      >
        ← {isTalentPool ? "Volver al portal" : "Volver al puesto"}
      </Link>

      <h1 className="font-jakarta text-2xl font-bold text-[#1A1A2E] mb-1">
        {isTalentPool ? "Cargá tu CV en el pool de talentos" : "Postularme"}
      </h1>
      <p className="text-sm text-[#6B7280] mb-8">
        {isTalentPool
          ? <>{company.name} · Te contactamos cuando publiquemos algo que encaje con tu perfil.</>
          : <>{job?.title} — {company.name}</>}
      </p>

      {draftBanner}
      {stepIndicator}

      {/* ════════════════ STEP 1: PERSONAL ════════════════════ */}
      {step === 1 && (
        <div className="space-y-6">
          {/* Photo */}
          <div className="flex flex-col items-center gap-2 pb-2">
            <button type="button" onClick={() => photoInputRef.current?.click()} disabled={photoUploading}
              className="relative flex h-20 w-20 items-center justify-center rounded-full border-2 border-dashed border-[#E5E7EB] bg-white hover:border-[#1B2A4A]/40 transition-colors overflow-hidden cursor-pointer disabled:opacity-60">
              {photoPreview ? (
                <img src={photoPreview} alt="Foto" className="h-full w-full object-cover" />
              ) : (
                <Camera size={22} className="text-[#9CA3AF]" />
              )}
              {photoUploading && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 text-[#6B7280]" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}
            </button>
            <p className="text-xs text-[#9CA3AF]">Foto de perfil (opcional)</p>
            {photoPreview && (
              <button type="button" onClick={() => { setPhotoPreview(null); setPhotoFile(null); setPhotoUrl(null); }}
                className="text-xs text-red-400 hover:text-red-500">Eliminar foto</button>
            )}
            <input ref={photoInputRef} type="file" accept="image/png,image/jpeg,image/webp"
              onChange={handlePhotoChange} className="sr-only" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Nombre" required>
              <TInput icon={User} placeholder="Juan" value={p.firstName} onChange={(v) => setP({ ...p, firstName: v })} required />
            </Field>
            <Field label="Apellido" required>
              <TInput placeholder="Pérez" value={p.lastName} onChange={(v) => setP({ ...p, lastName: v })} required />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Email" required>
              <TInput icon={Mail} type="email" placeholder="tu@email.com" value={p.email} onChange={(v) => setP({ ...p, email: v })} required />
            </Field>
            <Field label="Confirmar email" required>
              <TInput icon={Mail} type="email" placeholder="tu@email.com" value={p.emailConfirm}
                onChange={(v) => setP({ ...p, emailConfirm: v })} required />
            </Field>
          </div>

          {/* Layout re-ordenado para no dejar filas huérfanas — 14 fields
              en 7 pares. Orden actual respeta agrupación semántica:
              contacto → documento → nacimiento/género → residencia → dirección. */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Teléfono" required>
              <TInput icon={Phone} placeholder="+54 11 1234-5678" value={p.phone} onChange={(v) => setP({ ...p, phone: v })} required />
            </Field>
            <Field label="Fecha de nacimiento" required>
              <TInput icon={Calendar} type="date" value={p.birthDate}
                onChange={(v) => setP({ ...p, birthDate: v })}
                max={maxBirthDateForAdult()}
                required />
              <p className="text-[11px] text-[#9CA3AF] mt-1">
                Postulaciones disponibles solo para personas mayores de 18 años.
              </p>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Tipo de documento" required>
              <Sel
                options={[...DOCUMENT_TYPES]}
                value={p.documentType}
                onChange={(v) => setP({ ...p, documentType: (v as DocumentType) || "DNI" })}
                required
              />
            </Field>
            <Field
              label={p.documentType === "Pasaporte" ? "Número de pasaporte" : `Número de ${p.documentType.toLowerCase()}`}
              required
            >
              <TInput
                icon={CreditCard}
                placeholder={p.documentType === "Pasaporte" ? "AB1234567" : "12.345.678"}
                value={p.documentNumber}
                onChange={(v) => setP({ ...p, documentNumber: v })}
                required
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Género">
              <Sel options={GENDERS} value={p.gender} onChange={(v) => setP({ ...p, gender: v })} placeholder="Seleccioná..." />
            </Field>
            <Field label="Nacionalidad" required>
              <Sel options={[...NATIONALITIES]} value={p.nationality} onChange={(v) => setP({ ...p, nationality: v })} placeholder="Seleccioná tu nacionalidad..." />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="País de residencia" required>
              <select
                required
                value={p.residenceCountry}
                onChange={(e) => setP({
                  ...p,
                  residenceCountry: e.target.value as CountryCode,
                  // Cambio de país invalida provincia y ciudad — reset para
                  // que no queden "Buenos Aires" seleccionada estando el
                  // país en México.
                  province: "",
                  city: "",
                })}
                className="w-full px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A] text-[#1A1A2E]"
              >
                {RESIDENCE_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Provincia / Estado" required>
              <Sel
                options={[...getProvincesForCountry(p.residenceCountry)]}
                value={p.province}
                // Clear city if province changes so stale selections can't leak.
                onChange={(v) => setP({ ...p, province: v, city: "" })}
                placeholder="Seleccioná..."
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Ciudad / Localidad" required>
              <Typeahead
                options={p.province ? [...getCitiesForProvince(p.residenceCountry, p.province)] : []}
                value={p.city}
                onChange={(v) => setP({ ...p, city: v })}
                placeholder={p.province ? "Tipeá tu localidad o elegí de la lista..." : "Elegí una provincia primero"}
                disabled={!p.province}
                required
              />
            </Field>
            <Field label="Dirección">
              <TInput icon={Home} placeholder="Av. Corrientes 1234" value={p.address} onChange={(v) => setP({ ...p, address: v })} />
            </Field>
          </div>

          {error && <ErrorBox message={error} />}
          {navButtons()}
        </div>
      )}

      {/* ════════════════ STEP 2: EDUCATION + EXPERIENCE ══════ */}
      {step === 2 && (
        <div className="space-y-8">

          {/* ── Educación ──────────────────────────────────── */}
          <section className="space-y-4">
            <SectionTitle>Educación</SectionTitle>
            {education.map((ed) => (
              <EducationCard key={ed.id} entry={ed} onChange={changeEd} onRemove={removeEd} />
            ))}
            <button type="button" onClick={() => setEducation((prev) => [...prev, emptyEd()])}
              className="flex items-center gap-1.5 text-sm font-semibold hover:opacity-80 transition-opacity"
              style={{ color: accent }}>
              <Plus size={15} /> Añadir educación
            </button>

            <div className="pt-2">
              <p className="text-sm font-medium text-[#1A1A2E] mb-2">¿Tenés los estudios secundarios completos?</p>
              <div className="flex gap-5">
                {[["si","Sí"],["no","No"]].map(([val, lbl]) => (
                  <label key={val} className="flex items-center gap-2 cursor-pointer text-sm text-[#1A1A2E]">
                    <input type="radio" name="secondary" value={val}
                      checked={secondaryComplete === val}
                      onChange={() => setSecondaryComplete(val as "si" | "no")} />
                    {lbl}
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* ── Experiencia laboral ────────────────────────── */}
          <section className="space-y-4">
            <SectionTitle>Experiencia laboral</SectionTitle>
            {experience.map((exp) => (
              <ExperienceCard key={exp.id} entry={exp} onChange={changeExp} onRemove={removeExp} />
            ))}
            <button type="button" onClick={() => setExperience((prev) => [...prev, emptyExp()])}
              className="flex items-center gap-1.5 text-sm font-semibold hover:opacity-80 transition-opacity"
              style={{ color: accent }}>
              <Plus size={15} /> Añadir experiencia
            </button>
          </section>

          {/* ── Idiomas ────────────────────────────────────── */}
          <section className="space-y-4">
            <SectionTitle>Idiomas</SectionTitle>

            {languages.length > 0 && (
              <div className="rounded-xl border border-[#E5E7EB] overflow-hidden">
                {/* Header */}
                <div className="grid grid-cols-[1fr_1fr_1fr_32px] gap-3 px-4 py-2 bg-[#F8F9FA] border-b border-[#E5E7EB]">
                  <p className="text-xs font-semibold text-[#6B7280]">Idioma</p>
                  <p className="text-xs font-semibold text-[#6B7280]">Nivel oral</p>
                  <p className="text-xs font-semibold text-[#6B7280]">Nivel escrito</p>
                  <span />
                </div>
                {languages.map((lang) => (
                  <div key={lang.id} className="grid grid-cols-[1fr_1fr_1fr_32px] gap-3 items-center px-4 py-3 border-b border-[#F3F4F6] last:border-0">
                    <select value={lang.idioma} onChange={(e) => changeLang(lang.id, "idioma", e.target.value)}
                      className="w-full px-2 py-1.5 text-sm rounded-lg border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20">
                      <option value="">Seleccioná...</option>
                      {COMMON_LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <select value={lang.nivelOral} onChange={(e) => changeLang(lang.id, "nivelOral", e.target.value)}
                      className="w-full px-2 py-1.5 text-sm rounded-lg border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20">
                      <option value="">Nivel...</option>
                      {LANG_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <select value={lang.nivelEscrito} onChange={(e) => changeLang(lang.id, "nivelEscrito", e.target.value)}
                      className="w-full px-2 py-1.5 text-sm rounded-lg border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20">
                      <option value="">Nivel...</option>
                      {LANG_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <button type="button" onClick={() => removeLang(lang.id)}
                      className="flex items-center justify-center text-[#9CA3AF] hover:text-red-500 transition-colors">
                      <X size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button type="button" onClick={() => setLanguages((prev) => [...prev, emptyLang()])}
              className="flex items-center gap-1.5 text-sm font-semibold hover:opacity-80 transition-opacity"
              style={{ color: accent }}>
              <Plus size={15} /> Agregar idioma
            </button>
          </section>

          {/* ── Información adicional ──────────────────────── */}
          <section className="space-y-4">
            <SectionTitle>Información adicional</SectionTitle>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Pretensión salarial" required={requireSalaryExpectation}>
                <TInput icon={DollarSign} placeholder="Ej: USD 2.000 / ARS 800.000"
                  value={pro.salaryExpectation} onChange={(v) => setPro({ ...pro, salaryExpectation: v })}
                  required={requireSalaryExpectation} />
              </Field>
              <Field label="LinkedIn / Perfil profesional">
                <TInput icon={Globe} type="url" placeholder="https://linkedin.com/in/..."
                  value={pro.linkedinUrl} onChange={(v) => setPro({ ...pro, linkedinUrl: v })} />
              </Field>
            </div>

          </section>

          {/* ── CV ──────────────────────────────────────────── */}
          <section className="space-y-3">
            <SectionTitle>CV / Currículum</SectionTitle>
            <label className="flex items-center gap-3 w-full px-4 py-4 rounded-xl border-2 border-dashed border-[#E5E7EB] bg-white hover:border-[#1B2A4A]/40 cursor-pointer transition-colors">
              <Upload size={18} className="text-[#6B7280] flex-shrink-0" />
              <span className="text-sm text-[#6B7280] flex-1 truncate">
                {cvFile ? cvFile.name : "Seleccioná tu CV en PDF (máx. 5MB)..."}
              </span>
              {cvFile && (
                <button type="button" onClick={(e) => { e.preventDefault(); setCvFile(null); }}
                  className="text-[#6B7280] hover:text-[#D94F3D] flex-shrink-0">
                  <X size={14} />
                </button>
              )}
              <input ref={cvInputRef} type="file" accept="application/pdf" onChange={handleCvChange} className="sr-only" />
            </label>
            {cvError && <p className="text-xs text-red-500">{cvError}</p>}
          </section>

          {error && <ErrorBox message={error} />}
          {/* En apply mode mostramos navButtons ("Continuar" → step 3).
              En talent_pool ocultamos "Continuar" porque el bloque de abajo
              (consent + submit) hace de cierre. */}
          {navButtons(isTalentPool)}
          {/* Talent pool: consent + submit inline al final del step 2. No
              hay step 3 porque no hay custom questions del job. */}
          {isTalentPool && (
            <section className="rounded-2xl border border-[#E5E7EB] bg-white p-5 sm:p-6 space-y-4">
              <SectionTitle>Consentimiento y envío</SectionTitle>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={companyConsent}
                  onChange={(e) => setCompanyConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[#D1D5DB] flex-shrink-0"
                  style={{ accentColor: accent }}
                />
                <span className="text-xs text-[#374151] leading-relaxed">
                  {companyConsentText}
                </span>
              </label>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
                  disabled={submitting || !companyConsent}
                  className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
                  style={{ background: primary }}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Enviando…
                    </>
                  ) : (
                    <>Enviar mi CV al pool de talentos</>
                  )}
                </button>
              </div>
            </section>
          )}
        </div>
      )}

      {/* ════════════════ STEP 3: QUESTIONS + SUBMIT (solo apply mode) ══ */}
      {!isTalentPool && step === 3 && (
        <form onSubmit={handleSubmit} className="space-y-5">
          {questions.length > 0 ? (
            <div className="space-y-4">
              <SectionTitle>Preguntas adicionales</SectionTitle>
              {questions.map((q) => (
                <Field key={q.id} label={q.label} required={q.required}>
                  {q.type === "textarea" ? (
                    <textarea required={q.required} rows={3}
                      value={customAnswers[q.id] ?? ""}
                      onChange={(e) => setCustomAnswers({ ...customAnswers, [q.id]: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 resize-none" />
                  ) : q.type === "select" ? (
                    <select required={q.required} value={customAnswers[q.id] ?? ""}
                      onChange={(e) => setCustomAnswers({ ...customAnswers, [q.id]: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20">
                      <option value="">Seleccioná...</option>
                      {(q.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : q.type === "multiselect" ? (
                    <div className="flex flex-col gap-2">
                      {(q.options ?? []).map((opt) => {
                        const selected = (customAnswers[q.id] ?? "").split(",").map((s) => s.trim()).filter(Boolean);
                        const checked = selected.includes(opt);
                        return (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm">
                            <input type="checkbox" checked={checked}
                              onChange={() => {
                                const next = checked ? selected.filter((s) => s !== opt) : [...selected, opt];
                                setCustomAnswers({ ...customAnswers, [q.id]: next.join(", ") });
                              }} />
                            {opt}
                          </label>
                        );
                      })}
                    </div>
                  ) : q.type === "boolean" ? (
                    <div className="flex gap-4">
                      {["Sí","No"].map((opt) => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm">
                          <input type="radio" name={q.id} value={opt} required={q.required}
                            checked={customAnswers[q.id] === opt}
                            onChange={() => setCustomAnswers({ ...customAnswers, [q.id]: opt })} />
                          {opt}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <input type="text" required={q.required} value={customAnswers[q.id] ?? ""}
                      onChange={(e) => setCustomAnswers({ ...customAnswers, [q.id]: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm rounded-xl border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20" />
                  )}
                </Field>
              ))}
            </div>
          ) : (
            <div className="rounded-xl bg-[#F8F9FA] border border-[#E5E7EB] px-5 py-6 text-center">
              <CheckCircle size={28} className="mx-auto text-[#2A9D8F] mb-2" strokeWidth={1.5} />
              <p className="text-sm font-medium text-[#1A1A2E]">¡Todo listo!</p>
              <p className="text-xs text-[#6B7280] mt-1">Revisá tu información y enviá tu postulación.</p>
            </div>
          )}

          {/* Honeypot invisible — debe quedar vacío. Si un bot lo llena,
              el server descarta la submission silenciosamente. aria-hidden
              y tab-index negativo para que teclado/screen reader no lo toquen. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
            <label htmlFor="website_url_hp">Website</label>
            <input
              type="text"
              id="website_url_hp"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          {isTurnstileEnabled() && (
            <div className="flex justify-center">
              <Turnstile
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
                theme="light"
              />
            </div>
          )}

          <div className="space-y-2.5 pt-1">
            {/* Casilla 1 — obligatoria (procesamiento por la empresa) */}
            <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-[#F9FAFB] border border-[#E5E7EB] px-4 py-3">
              <input
                type="checkbox"
                checked={companyConsent}
                onChange={(e) => setCompanyConsent(e.target.checked)}
                className="mt-0.5 rounded border-[#D1D5DB] flex-shrink-0"
                required
              />
              <span className="text-xs text-[#374151] leading-relaxed">
                <span className="text-red-600 font-semibold mr-0.5">*</span>
                He leído y acepto la{" "}
                <a href="/es/privacy" target="_blank" rel="noopener" className="underline font-semibold hover:text-[#0B0F1A]">
                  política de privacidad
                </a>
                {" "}de <strong>{company.name}</strong>.
              </span>
            </label>

            {/* Casilla 2 — opcional en el flow pero sin etiquetar "opcional"
                literalmente. Solo se renderiza si la empresa tiene pool de
                talentos habilitado (plan Growth/Scale, o toggle manual del
                superadmin). Habilita a ESTA empresa a guardar el perfil en
                SU pool por 12 meses (no se comparte cross-tenant). */}
            {talentPoolAvailable && (
              <label className="flex items-start gap-3 cursor-pointer rounded-xl bg-[#F9FAFB] border border-[#E5E7EB] px-4 py-3">
                <input
                  type="checkbox"
                  checked={benchConsent}
                  onChange={(e) => setBenchConsent(e.target.checked)}
                  className="mt-0.5 rounded border-[#D1D5DB] flex-shrink-0"
                />
                <span className="text-xs text-[#374151] leading-relaxed">
                  Acepto que <strong>{company.name}</strong> también guarde mi perfil
                  en su pool de talentos por 12 meses, para considerarme en futuras
                  búsquedas similares. Mis datos <strong>no se comparten con otras
                  empresas</strong>. Puedo revocar este consentimiento o solicitar el
                  borrado en cualquier momento desde el link al pie de cualquier email.
                  <span className="block mt-1 text-[11px] text-[#9CA3AF]">
                    Tu postulación a <strong>{job?.title}</strong> se envía igual si no lo marcás.
                  </span>
                </span>
              </label>
            )}
          </div>

          {error && <ErrorBox message={error} />}

          <div className="flex justify-between pt-2">
            <button type="button" onClick={handleBack}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-[#6B7280] border border-[#E5E7EB] bg-white hover:bg-gray-50 transition-colors">
              <ArrowLeft size={15} /> Atrás
            </button>
            <button type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-xl text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity"
              style={{ backgroundColor: accent }}>
              {submitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Enviando...
                </>
              ) : "Enviar postulación"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
